import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { normalizeBarcode } from '../utils/normalizeBarcode';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../components/ui/hover-card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Search, 
  Bike, 
  Trash2, 
  Eye,
  Filter,
  Euro,
  ImageIcon,
  CheckCircle2,
  ShoppingCart,
  CircleDot,
  Zap,
  Printer,
  Settings2,
  ScanBarcode,
  AlertTriangle,
  Clock,
  UserCheck,
  Check,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PrintLabelModal from '../components/PrintLabelModal';
import ExportBicyclesModal from '../components/ExportBicyclesModal';
import LokkiExportModal from '../components/LokkiExportModal';
import { Checkbox } from '../components/ui/checkbox';
import { ModelsManager, TallasManager } from '../components/CatalogManager';
import BarcodeScannerModal from '../components/BarcodeScannerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Bicycles() {
  const { isAdmin, activeEmpresaNombre } = useAuth();
  const [bicycles, setBicycles] = useState([]);
  const [allBicycles, setAllBicycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('activa');
  const [modelFilter, setModelFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('default'); // 'default' | 'recent' | 'oldest'
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isPrintLabelOpen, setIsPrintLabelOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isLokkiOpen, setIsLokkiOpen] = useState(false);
  const [selectedBikeIds, setSelectedBikeIds] = useState(new Set());
  
  // Catalog state
  const [catalogModels, setCatalogModels] = useState([]);
  const [catalogTallas, setCatalogTallas] = useState([]);
  const [isModelsOpen, setIsModelsOpen] = useState(false);
  const [isTallasOpen, setIsTallasOpen] = useState(false);
  
  // New bike form
  const [newBike, setNewBike] = useState({
    modelo_id: '',
    size: '',
    sequence_number: '',
    manual_number: false,
    number: ''
  });
  const [selectedModel, setSelectedModel] = useState(null);
  const [nextSeq, setNextSeq] = useState(null);
  const [codePreview, setCodePreview] = useState('');
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const lastKeyTime = useRef(0);
  const scanBuffer = useRef('');
  const scanTimeout = useRef(null);

  // Get unique models and sizes for filters
  const uniqueModels = [...new Set(allBicycles.map(b => b.model))].sort();
  const uniqueSizes = [...new Set(allBicycles.map(b => b.size))].sort();

  useEffect(() => {
    fetchAllBicycles();
    fetchCatalog();
  }, []);

  useEffect(() => {
    fetchBicycles();
  }, [search, statusFilter]);

  const fetchCatalog = async () => {
    try {
      const [mRes, tRes] = await Promise.all([
        axios.get(`${API}/catalog/models`),
        axios.get(`${API}/catalog/tallas`)
      ]);
      setCatalogModels(mRes.data);
      setCatalogTallas(tRes.data);
    } catch { /* ignore */ }
  };

  const fetchAllBicycles = async () => {
    try {
      const response = await axios.get(`${API}/bicycles`);
      setAllBicycles(response.data);
    } catch (error) {
      console.error('Error fetching all bicycles:', error);
    }
  };

  const fetchBicycles = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/bicycles?${params}`);
      setBicycles(response.data);
    } catch (error) {
      toast.error('Error al cargar las bicicletas');
    } finally {
      setLoading(false);
    }
  };

  // When model selection changes, fetch next sequence
  const handleModelSelect = async (modelId) => {
    const model = catalogModels.find(m => m.id === modelId);
    setSelectedModel(model);
    setNewBike(prev => ({ ...prev, modelo_id: modelId }));
    if (model) {
      try {
        const res = await axios.get(`${API}/catalog/next-sequence/${model.numero_modelo}`);
        setNextSeq(res.data.next_sequence);
        setNewBike(prev => ({ ...prev, sequence_number: String(res.data.next_sequence) }));
      } catch { setNextSeq(null); }
    }
  };

  // Update code preview
  useEffect(() => {
    if (selectedModel && newBike.size && newBike.sequence_number) {
      setCodePreview(`${selectedModel.numero_modelo}(${newBike.size})${newBike.sequence_number}`);
    } else {
      setCodePreview('');
    }
  }, [selectedModel, newBike.size, newBike.sequence_number]);

  const handleAddBike = async (e) => {
    e.preventDefault();
    if (!newBike.modelo_id || !newBike.size) {
      toast.error('Selecciona un modelo y una talla');
      return;
    }
    try {
      const payload = {
        modelo_id: newBike.modelo_id,
        size: newBike.size,
        sequence_number: newBike.sequence_number ? parseInt(newBike.sequence_number) : undefined,
        manual_number: newBike.manual_number,
        number: newBike.manual_number ? newBike.number : ''
      };
      await axios.post(`${API}/bicycles`, payload);
      toast.success('Bicicleta añadida correctamente');
      setIsAddDialogOpen(false);
      setNewBike({ modelo_id: '', size: '', sequence_number: '', manual_number: false, number: '' });
      setSelectedModel(null);
      setNextSeq(null);
      setCodePreview('');
      fetchBicycles();
      fetchAllBicycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir la bicicleta');
    }
  };

  const handleDeleteBike = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/bicycles/${deleteId}`);
      toast.success('Bicicleta eliminada');
      setDeleteId(null);
      fetchBicycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      activa: { label: 'Activa', class: 'bg-blue-100 text-blue-800 border-blue-200' },
      en_taller: { label: 'En Taller', class: 'bg-orange-100 text-orange-800 border-orange-200' },
      'en revisión': { label: 'En revisión', class: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      rota: { label: 'Rota', class: 'bg-red-100 text-red-800 border-red-200' },
      prestada: { label: 'Prestada', class: 'bg-purple-100 text-purple-800 border-purple-200' },
      retirada: { label: 'Retirada', class: 'bg-slate-100 text-slate-600 border-slate-200' },
      vendida: { label: 'Vendida', class: 'bg-green-100 text-green-800 border-green-200' },
      reservada: { label: 'Reservada (Tienda)', class: 'bg-sky-100 text-sky-800 border-sky-200' },
      robada: { label: 'Robada', class: 'bg-red-100 text-red-800 border-red-200' }
    };
    const config = statusConfig[status] || statusConfig.activa;
    return (
      <Badge variant="outline" className={config.class}>
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  // Handle barcode scan result (from camera or external scanner)
  const handleBarcodeScan = useCallback(async (code) => {
    if (!code) return;
    // 1) Deterministic backend resolution (frame_number, number or internal_code;
    //    exact or normalized) — works for old number-QRs and new frame-number-QRs.
    try {
      const resolveRes = await axios.get(`${API}/bicycles/scan/resolve?code=${encodeURIComponent(code.trim())}`);
      if (resolveRes.data?.match?.id) {
        const b = resolveRes.data.match;
        navigate(`/bicycles/${b.id}`);
        toast.success(`Bicicleta encontrada: ${b.model} - ${b.number}`);
        return;
      }
    } catch { /* fall through to legacy search */ }
    const { normalized, stripped, variants } = normalizeBarcode(code);
    // Search with both normalized and stripped versions
    const searchTerms = [...new Set([normalized, stripped, code.trim()])];
    try {
      // Try each search term until we get results
      let bikes = [];
      for (const term of searchTerms) {
        const res = await axios.get(`${API}/bicycles?search=${encodeURIComponent(term)}`);
        if (res.data.length > 0) {
          bikes = res.data;
          break;
        }
      }
      // Exact match: check all variants against number/internal_code
      const exact = bikes.find(b => {
        const num = (b.number || '').toLowerCase();
        const ic = (b.internal_code || '').toLowerCase();
        const numStripped = num.replace(/[^a-z0-9]/g, '');
        const icStripped = ic.replace(/[^a-z0-9]/g, '');
        return variants.some(v => {
          const vl = v.toLowerCase();
          return num === vl || ic === vl || numStripped === vl || icStripped === vl;
        });
      });
      if (exact) {
        navigate(`/bicycles/${exact.id}`);
        toast.success(`Bicicleta encontrada: ${exact.model} - ${exact.number}`);
      } else if (bikes.length === 1) {
        navigate(`/bicycles/${bikes[0].id}`);
        toast.success(`Bicicleta encontrada: ${bikes[0].model} - ${bikes[0].number}`);
      } else if (bikes.length > 1) {
        // Partial fallback: try matching by sequence number
        const seqMatch = normalized.match(/\)(\d+)$/) || normalized.match(/(\d{2,})$/);
        if (seqMatch) {
          const seq = seqMatch[1];
          const seqBike = bikes.find(b => {
            const m = (b.number || '').match(/\)(\d+)$/);
            return m && m[1] === seq;
          });
          if (seqBike) {
            navigate(`/bicycles/${seqBike.id}`);
            toast.success(`Bicicleta encontrada: ${seqBike.model} - ${seqBike.number}`);
            return;
          }
        }
        setSearch(normalized);
        toast.info(`${bikes.length} resultados para "${normalized}"`);
      } else {
        setSearch(normalized);
        toast.error(`No se encontró bicicleta con código "${normalized}"`);
      }
    } catch {
      setSearch(normalized);
      toast.error('Error al buscar bicicleta');
    }
  }, [navigate]);

  // External barcode scanner detection: rapid keystrokes + Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only capture when no modal/dialog is open and not focused on a textarea
      const tag = document.activeElement?.tagName;
      if (tag === 'TEXTAREA') return;
      if (isAddDialogOpen || isModelsOpen || isTallasOpen || isPrintLabelOpen || isScannerOpen) return;

      const now = Date.now();
      const timeDiff = now - lastKeyTime.current;
      lastKeyTime.current = now;

      if (e.key === 'Enter') {
        // If buffer has enough characters (scanner sends fast), treat as scan
        if (scanBuffer.current.length >= 3) {
          e.preventDefault();
          handleBarcodeScan(scanBuffer.current);
          scanBuffer.current = '';
          if (scanTimeout.current) clearTimeout(scanTimeout.current);
        }
        return;
      }

      // Printable character
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (timeDiff < 80) {
          // Fast input — likely scanner
          scanBuffer.current += e.key;
        } else {
          // Normal typing — reset buffer
          scanBuffer.current = e.key;
        }
        // Clear buffer after 200ms of no input
        if (scanTimeout.current) clearTimeout(scanTimeout.current);
        scanTimeout.current = setTimeout(() => { scanBuffer.current = ''; }, 200);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBarcodeScan, isAddDialogOpen, isModelsOpen, isTallasOpen, isPrintLabelOpen, isScannerOpen]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  // Apply model and size filters to the bicycles list
  const filteredBicycles = bicycles
    .filter(bike => {
      const matchesModel = modelFilter === 'all' || bike.model === modelFilter;
      const matchesSize = sizeFilter === 'all' || bike.size === sizeFilter;
      return matchesModel && matchesSize;
    })
    .sort((a, b) => {
      if (sortOrder === 'default') return 0;
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortOrder === 'recent' ? db - da : da - db;
    });

  // Contextual summary of the current view (status + model + size + search)
  const STATUS_LABELS = {
    all: 'Todas', activa: 'Activas', en_taller: 'En Taller', 'en revisión': 'En revisión',
    rota: 'Rotas', prestada: 'Prestadas', vendida: 'Vendidas', retirada: 'Retiradas', robada: 'Robadas',
  };
  const viewFilterParts = [];
  viewFilterParts.push(STATUS_LABELS[statusFilter] || statusFilter);
  if (modelFilter !== 'all') viewFilterParts.push(modelFilter);
  if (sizeFilter !== 'all') viewFilterParts.push(`Talla ${sizeFilter}`);
  if (search) viewFilterParts.push(`"${search}"`);
  const viewFilterLabel = viewFilterParts.join(' · ');

  // Selection helpers
  const toggleBikeSelection = (id) => {
    setSelectedBikeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllFiltered = () => {
    const allIds = new Set(filteredBicycles.map(b => b.id));
    const allSelected = filteredBicycles.every(b => selectedBikeIds.has(b.id));
    setSelectedBikeIds(allSelected ? new Set() : allIds);
  };
  const hasSelection = selectedBikeIds.size > 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="bicycles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900">
            Bicicletas
          </h1>
          <p className="text-slate-500 mt-1">Gestiona tu flota de bicicletas</p>
        </div>
        
        <div className="flex gap-2">
          {/* Print Labels Button */}
          <Button 
            variant="outline"
            className="gap-2"
            onClick={() => setIsPrintLabelOpen(true)}
            data-testid="print-labels-btn"
          >
            <Printer size={18} />
            Imprimir etiquetas
            {hasSelection && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 ml-1">
                {selectedBikeIds.size}
              </Badge>
            )}
          </Button>

          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setIsExportOpen(true)}
            data-testid="export-list-btn"
          >
            <Download size={18} />
            Lista
          </Button>
          
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setIsLokkiOpen(true)}
            data-testid="lokki-export-btn"
          >
            <Download size={18} />
            Lokki
            {hasSelection && (
              <Badge variant="secondary" className="bg-green-100 text-green-700 ml-1">
                {selectedBikeIds.size}
              </Badge>
            )}
          </Button>

          {isAdmin && (
          <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
            setIsAddDialogOpen(open);
            if (!open) {
              setNewBike({ modelo_id: '', size: '', sequence_number: '', manual_number: false, number: '' });
              setSelectedModel(null); setNextSeq(null); setCodePreview('');
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                data-testid="add-bicycle-btn"
              >
                <Plus size={18} />
                Añadir Bicicleta
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Barlow_Condensed']">Nueva Bicicleta</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddBike} className="space-y-4">
              {/* Model dropdown */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Modelo</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-orange-600"
                    onClick={() => setIsModelsOpen(true)} data-testid="manage-models-btn">
                    <Settings2 className="w-3 h-3 mr-1" /> Gestionar
                  </Button>
                </div>
                <Select value={newBike.modelo_id} onValueChange={handleModelSelect}>
                  <SelectTrigger data-testid="bike-model-select">
                    <SelectValue placeholder="Seleccionar modelo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogModels.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        <span className="font-mono text-orange-600 mr-2">{m.numero_modelo}</span>
                        {m.nombre_modelo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Size dropdown */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Talla</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-orange-600"
                    onClick={() => setIsTallasOpen(true)} data-testid="manage-tallas-btn">
                    <Settings2 className="w-3 h-3 mr-1" /> Gestionar
                  </Button>
                </div>
                <Select value={newBike.size} onValueChange={(v) => setNewBike(prev => ({ ...prev, size: v }))}>
                  <SelectTrigger data-testid="bike-size-select">
                    <SelectValue placeholder="Seleccionar talla..." />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogTallas.map(t => (
                      <SelectItem key={t.id} value={t.talla}>{t.talla}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sequence number */}
              <div className="space-y-2">
                <Label>Número (consecutivo)</Label>
                <Input
                  type="number" min="1"
                  value={newBike.sequence_number}
                  onChange={(e) => setNewBike(prev => ({ ...prev, sequence_number: e.target.value }))}
                  placeholder={nextSeq ? `Siguiente: ${nextSeq}` : 'Auto'}
                  data-testid="bike-sequence-input"
                />
                <p className="text-xs text-slate-400">
                  Se genera automáticamente. Puedes modificarlo manualmente.
                </p>
              </div>

              {/* Code preview */}
              {codePreview && (
                <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="code-preview">
                  <p className="text-xs text-slate-500 mb-1">Código generado</p>
                  <p className="text-2xl font-mono font-bold text-orange-600">{codePreview}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {selectedModel?.nombre_modelo}
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white" 
                  disabled={!newBike.modelo_id || !newBike.size}
                  data-testid="save-bike-btn">
                  Guardar Bicicleta
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
        </div>
      </div>

      {/* Print Label Modal */}
      <PrintLabelModal
        open={isPrintLabelOpen}
        onClose={() => setIsPrintLabelOpen(false)}
        bicycles={allBicycles.filter(b => b.status === 'activa')}
        preSelectedIds={[...selectedBikeIds]}
      />

      <LokkiExportModal
        open={isLokkiOpen}
        onClose={() => setIsLokkiOpen(false)}
        bicycles={allBicycles}
        preSelectedIds={[...selectedBikeIds]}
      />

      <ExportBicyclesModal
        open={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        bicycles={allBicycles}
        activeEmpresaNombre={activeEmpresaNombre}
      />

      {/* Catalog Management Modals */}
      <ModelsManager open={isModelsOpen} onClose={() => setIsModelsOpen(false)} onRefresh={fetchCatalog} />
      <TallasManager open={isTallasOpen} onClose={() => setIsTallasOpen(false)} onRefresh={fetchCatalog} />

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        open={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScan={handleBarcodeScan}
      />

      {/* Status Quick Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('all')}
          className={statusFilter === 'all' ? 'bg-slate-800 hover:bg-slate-900' : ''}
          data-testid="filter-all"
        >
          <CircleDot size={16} className="mr-2" />
          Todas
          <Badge variant="secondary" className="ml-2 bg-slate-200 text-slate-700">
            {allBicycles.length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'activa' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('activa')}
          className={statusFilter === 'activa' ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'}
          data-testid="filter-activa"
        >
          <CheckCircle2 size={16} className="mr-2" />
          Activas
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'activa' ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-700'}`}>
            {allBicycles.filter(b => b.status === 'activa').length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'en_taller' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('en_taller')}
          className={statusFilter === 'en_taller' ? 'bg-orange-500 hover:bg-orange-600' : 'hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300'}
          data-testid="filter-taller"
        >
          <Bike size={16} className="mr-2" />
          En Taller
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'en_taller' ? 'bg-orange-400 text-white' : 'bg-orange-100 text-orange-700'}`}>
            {allBicycles.filter(b => b.status === 'en_taller').length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'en revisión' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('en revisión')}
          className={statusFilter === 'en revisión' ? 'bg-yellow-500 hover:bg-yellow-600' : 'hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-300'}
          data-testid="filter-revision"
        >
          <CircleDot size={16} className="mr-2" />
          En revisión
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'en revisión' ? 'bg-yellow-400 text-white' : 'bg-yellow-100 text-yellow-700'}`}>
            {allBicycles.filter(b => b.status === 'en revisión').length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'rota' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('rota')}
          className={statusFilter === 'rota' ? 'bg-red-600 hover:bg-red-700' : 'hover:bg-red-50 hover:text-red-700 hover:border-red-300'}
          data-testid="filter-rota"
        >
          <AlertTriangle size={16} className="mr-2" />
          Rotas
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'rota' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700'}`}>
            {allBicycles.filter(b => b.status === 'rota').length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'prestada' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('prestada')}
          className={statusFilter === 'prestada' ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300'}
          data-testid="filter-prestada"
        >
          <UserCheck size={16} className="mr-2" />
          Prestadas
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'prestada' ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-700'}`}>
            {allBicycles.filter(b => b.status === 'prestada').length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'vendida' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('vendida')}
          className={statusFilter === 'vendida' ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:text-green-700 hover:border-green-300'}
          data-testid="filter-vendida"
        >
          <ShoppingCart size={16} className="mr-2" />
          Vendidas
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'vendida' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'}`}>
            {allBicycles.filter(b => b.status === 'vendida').length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'retirada' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('retirada')}
          className={statusFilter === 'retirada' ? 'bg-slate-600 hover:bg-slate-700' : 'hover:bg-slate-100'}
          data-testid="filter-retirada"
        >
          Retiradas
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'retirada' ? 'bg-slate-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
            {allBicycles.filter(b => b.status === 'retirada').length}
          </Badge>
        </Button>
        <Button
          variant={statusFilter === 'robada' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('robada')}
          className={statusFilter === 'robada' ? 'bg-red-700 hover:bg-red-800' : 'hover:bg-red-50 hover:text-red-700 hover:border-red-300'}
          data-testid="filter-robada"
        >
          Robadas
          <Badge variant="secondary" className={`ml-2 ${statusFilter === 'robada' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700'}`}>
            {allBicycles.filter(b => b.status === 'robada').length}
          </Badge>
        </Button>
      </div>

      {/* Search and Additional Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  ref={searchInputRef}
                  placeholder="Buscar por código, modelo, talla, nº bastidor o prestada a..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && search.trim()) {
                      handleBarcodeScan(search.trim());
                    }
                  }}
                  data-testid="search-input"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="flex-shrink-0 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                onClick={() => setIsScannerOpen(true)}
                title="Escanear código de barras"
                data-testid="scan-barcode-btn"
              >
                <ScanBarcode size={20} />
              </Button>
            </div>
            <Select value={modelFilter} onValueChange={setModelFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="model-filter">
                <Bike size={16} className="mr-2" />
                <SelectValue placeholder="Modelo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los modelos</SelectItem>
                {uniqueModels.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sizeFilter} onValueChange={setSizeFilter}>
              <SelectTrigger className="w-full sm:w-36" data-testid="size-filter">
                <SelectValue placeholder="Talla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las tallas</SelectItem>
                {uniqueSizes.map((size) => (
                  <SelectItem key={size} value={size}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-full sm:w-52" data-testid="sort-order">
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Orden por defecto</SelectItem>
                <SelectItem value="recent">Alta: más recientes primero</SelectItem>
                <SelectItem value="oldest">Alta: más antiguas primero</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results total summary */}
      {!loading && (
        <div className="flex items-center justify-between flex-wrap gap-2 px-1" data-testid="bicycles-total-summary">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Mostrando</span>
            <Badge className="bg-slate-900 text-white text-sm px-2.5 py-0.5" data-testid="bicycles-total-count">
              {filteredBicycles.length}
            </Badge>
            <span>{filteredBicycles.length === 1 ? 'bicicleta' : 'bicicletas'}</span>
            <span className="text-slate-400">·</span>
            <span className="font-medium text-slate-700">{viewFilterLabel}</span>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="border-slate-200" data-testid="bicycles-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner w-8 h-8" />
            </div>
          ) : filteredBicycles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Bike size={48} className="mb-4" />
              <p>No hay bicicletas que coincidan</p>
              <p className="text-sm">Prueba cambiando los filtros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10">
                      <Checkbox 
                        checked={filteredBicycles.length > 0 && filteredBicycles.every(b => selectedBikeIds.has(b.id))}
                        onCheckedChange={toggleAllFiltered}
                        data-testid="select-all-bikes"
                      />
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500 w-16">Foto</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Código</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Modelo</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Talla</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Estado</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Prestada a</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500 text-center" title="Publicada en la Tienda Online">Tienda Online</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Alta</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Revisiones</TableHead>
                    {isAdmin && (
                      <TableHead className="text-xs font-bold uppercase text-slate-500">Precio Compra</TableHead>
                    )}
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Coste Mant.</TableHead>
                    {statusFilter === 'vendida' && isAdmin && (
                      <>
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Precio Venta</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Beneficio</TableHead>
                      </>
                    )}
                    <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBicycles.map((bike) => (
                    <TableRow key={bike.id} className="table-row-hover" data-testid={`bike-row-${bike.id}`}>
                      <TableCell className="w-10">
                        <Checkbox 
                          checked={selectedBikeIds.has(bike.id)}
                          onCheckedChange={() => toggleBikeSelection(bike.id)}
                          data-testid={`select-bike-${bike.id}`}
                        />
                      </TableCell>
                      <TableCell className="w-16">
                        {bike.photo_url ? (
                          <HoverCard openDelay={200} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <div className="cursor-pointer">
                                <img 
                                  src={`${BACKEND_URL}${bike.photo_url}`}
                                  alt={`Bici ${bike.number}`}
                                  className="w-12 h-12 object-cover rounded-lg border border-slate-200 transition-transform hover:scale-105"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.nextSibling.style.display = 'flex';
                                  }}
                                />
                              </div>
                            </HoverCardTrigger>
                            <HoverCardContent 
                              side="right" 
                              align="start" 
                              className="w-80 p-2"
                            >
                              <div className="space-y-2">
                                <img 
                                  src={`${BACKEND_URL}${bike.photo_url}`}
                                  alt={`Bici ${bike.number}`}
                                  className="w-full h-auto rounded-lg object-contain max-h-64"
                                />
                                <div className="text-center">
                                  <p className="font-semibold text-slate-900">{bike.model}</p>
                                  <p className="text-sm text-slate-500 font-mono">{bike.number} - Talla {bike.size}</p>
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        ) : null}
                        <div 
                          className={`w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center ${bike.photo_url ? 'hidden' : ''}`}
                        >
                          <ImageIcon size={20} className="text-slate-400" />
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-slate-900">
                        <div className="flex items-center gap-2">
                          <code className="text-orange-600 font-mono">{bike.number}</code>
                          {bike.is_electric && (
                            <span className="flex items-center justify-center w-5 h-5 bg-yellow-100 rounded-full" title="Bicicleta Eléctrica">
                              <Zap size={12} className="text-yellow-600" />
                            </span>
                          )}
                          {bike.status === 'rota' && (
                            <span className="flex items-center justify-center w-5 h-5 bg-red-100 rounded-full" title={bike.status_comment || 'Rota'} data-testid={`broken-icon-${bike.id}`}>
                              <AlertTriangle size={12} className="text-red-600" />
                            </span>
                          )}
                          {bike.status === 'prestada' && (
                            <span className="flex items-center justify-center w-5 h-5 bg-purple-100 rounded-full" title={`Prestada a: ${bike.loan_person_name || 'Desconocido'}`} data-testid={`loaned-icon-${bike.id}`}>
                              <UserCheck size={12} className="text-purple-600" />
                            </span>
                          )}
                          {bike.revision_overdue && (
                            <span className="flex items-center justify-center w-5 h-5 bg-amber-100 rounded-full" title={`${bike.days_since_revision != null ? bike.days_since_revision + ' días sin revisión' : 'Sin revisiones'}`} data-testid={`overdue-icon-${bike.id}`}>
                              <Clock size={12} className="text-amber-600" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{bike.model}</TableCell>
                      <TableCell className="text-slate-600">{bike.size}</TableCell>
                      <TableCell>{getStatusBadge(bike.status)}</TableCell>
                      <TableCell className="text-center" data-testid={`shop-flag-${bike.id}`}>
                        {bike.shop?.published ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700" title={bike.shop?.reserved ? 'Publicada · reservada' : 'Publicada en la Tienda Online'}>
                            <Check size={14} strokeWidth={3} />
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`loan-info-${bike.id}`}>
                        {bike.status === 'prestada' && bike.loan_person_name ? (
                          <div className="flex flex-col leading-tight">
                            <span className="font-medium text-purple-700 flex items-center gap-1">
                              <UserCheck size={12} />
                              {bike.loan_person_name}
                            </span>
                            {bike.loan_date && (
                              <span className="text-xs text-slate-500">
                                Desde: {formatDate(bike.loan_date)}
                              </span>
                            )}
                            {bike.loan_return_date && (() => {
                              const today = new Date().toISOString().split('T')[0];
                              const isOverdue = bike.loan_return_date < today;
                              const isDueToday = bike.loan_return_date === today;
                              return (
                                <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : isDueToday ? 'text-amber-600 font-semibold' : 'text-slate-500'}`}>
                                  Devolución: {new Date(bike.loan_return_date + 'T00:00:00').toLocaleDateString('es-ES')}
                                </span>
                              );
                            })()}
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{formatDate(bike.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-50">
                          {bike.revision_count}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="font-medium">
                          {bike.purchase_price ? (
                            <span className="flex items-center gap-1 text-blue-700">
                              <Euro size={14} />
                              {formatCurrency(bike.purchase_price)}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-1 text-slate-700">
                          <Euro size={14} />
                          {formatCurrency(bike.total_maintenance_cost)}
                        </span>
                      </TableCell>
                      {statusFilter === 'vendida' && isAdmin && (
                        <>
                          <TableCell className="font-medium">
                            {bike.sold_price ? (
                              <span className="flex items-center gap-1 text-green-700">
                                <Euro size={14} />
                                {formatCurrency(bike.sold_price)}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {bike.profit !== null && bike.profit !== undefined ? (
                              <span className={`flex items-center gap-1 ${bike.profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                                <Euro size={14} />
                                {formatCurrency(bike.profit)}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link 
                            to={`/bicycles/${bike.id}`}
                            onClick={() => {
                              // Store filtered bike IDs for navigation in detail page
                              const bikeIds = filteredBicycles.map(b => b.id);
                              localStorage.setItem('bicycleNavList', JSON.stringify(bikeIds));
                            }}
                          >
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="gap-1"
                              data-testid={`view-bike-${bike.id}`}
                            >
                              <Eye size={14} />
                              Ver
                            </Button>
                          </Link>
                          {isAdmin && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => setDeleteId(bike.id)}
                              data-testid={`delete-bike-${bike.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Eliminar Bicicleta</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que quieres eliminar esta bicicleta? Se eliminarán también todas sus revisiones.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteBike}
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
